import * as React from 'react';

/** Повторные замеры после события: chrome/sticky могут доезжать без нового resize. */
const SETTLE_DELAYS_MS = [0, 100, 250, 500] as const;

/**
 * Хук, возвращающий максимальную высоту модалки до нижней границы элемента, переданного через пропсы в виде рефа
 * @param anchorRef
 * @param remeasureKey - смена ключа (например mode шторки) форсирует пересчёт; якорь может сдвинуться без resize
 */
export const useMaxModalHeight = <T extends HTMLElement>(
  anchorRef: React.MutableRefObject<T | null>,
  remeasureKey?: unknown,
) => {
  const [maxHeight, setMaxHeight] = React.useState(0);

  React.useLayoutEffect(() => {
    const update = () => {
      const el = anchorRef.current;
      if (!el) {
        return;
      }

      const { top, height } = el.getBoundingClientRect();
      const next = Math.max(0, Math.round(window.innerHeight - top - height + 1));

      setMaxHeight(prev => (prev === next ? prev : next));
    };

    let settleTimers: number[] = [];

    const clearSettle = () => {
      settleTimers.forEach(id => window.clearTimeout(id));
      settleTimers = [];
    };

    const onLayoutMaybeChanged = () => {
      update();
      clearSettle();
      settleTimers = SETTLE_DELAYS_MS.map(ms => window.setTimeout(update, ms));
    };

    onLayoutMaybeChanged();

    const rafId = window.requestAnimationFrame(() => {
      update();
      window.requestAnimationFrame(update);
    });

    window.addEventListener('resize', onLayoutMaybeChanged);
    window.addEventListener('scroll', onLayoutMaybeChanged, true);
    window.addEventListener('orientationchange', onLayoutMaybeChanged);
    window.visualViewport?.addEventListener('resize', onLayoutMaybeChanged);
    window.visualViewport?.addEventListener('scroll', onLayoutMaybeChanged);

    let resizeObserver: ResizeObserver | null = null;
    const el = anchorRef.current;
    if (el && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(onLayoutMaybeChanged);
      resizeObserver.observe(el);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      clearSettle();
      window.removeEventListener('resize', onLayoutMaybeChanged);
      window.removeEventListener('scroll', onLayoutMaybeChanged, true);
      window.removeEventListener('orientationchange', onLayoutMaybeChanged);
      window.visualViewport?.removeEventListener('resize', onLayoutMaybeChanged);
      window.visualViewport?.removeEventListener('scroll', onLayoutMaybeChanged);
      resizeObserver?.disconnect();
    };
  }, [anchorRef, remeasureKey]);

  return maxHeight;
};
